<script setup>
import { computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'

const page = usePage()
const notice = computed(() => page.props.flash?.notice)

// Tenant switcher — "which tenant am I acting as". The active tenant is shared
// app-wide (server session); switching persists across pages + reloads.
const ALL = 'all'
const tenantItems = computed(() => [
  { label: 'All tenants', value: ALL },
  ...(page.props.tenants ?? []).map((t) => ({ label: t.name, value: t.slug })),
])
const activeTenant = computed(() => page.props.active_tenant ?? ALL)
const switchTenant = (value) =>
  router.patch('/active_tenant', { tenant: value === ALL ? null : value }, { preserveScroll: true })

// Inertia's url carries the query string (e.g. "/assets?tenant=mg"); compare
// on the path only. The Library index renders at both "/" and "/assets".
const path = computed(() => page.url.split('?')[0])

const isActive = (to) => {
  const p = path.value
  if (to === '/assets') return p === '/' || p === '/assets'
  return p === to
}

// active is set explicitly because this app uses the Inertia router, not
// vue-router — UNavigationMenu can't auto-detect the current route here.
const items = computed(() => [
  { label: 'Library', icon: 'i-lucide-images', to: '/assets', active: isActive('/assets') },
  { label: 'URL generator', icon: 'i-lucide-link-2', to: '/generator', active: isActive('/generator') },
  { label: 'Playground', icon: 'i-lucide-sliders-horizontal', to: '/playground', active: isActive('/playground') },
  { label: 'Upload', icon: 'i-lucide-upload', to: '/assets/new', active: isActive('/assets/new') },
])
</script>

<template>
  <UApp>
    <div class="isolate flex min-h-screen bg-default text-default">
      <aside class="flex w-60 shrink-0 flex-col gap-4 border-e border-default p-4">
        <h1 class="px-2.5 font-semibold">Media Hub</h1>

        <div class="space-y-1">
          <p class="px-2.5 text-xs font-medium text-muted">Acting as</p>
          <USelect
            :model-value="activeTenant"
            :items="tenantItems"
            icon="i-lucide-building-2"
            class="w-full"
            @update:model-value="switchTenant"
          />
        </div>

        <UNavigationMenu :items="items" orientation="vertical" highlight />
      </aside>

      <main class="mx-auto max-w-5xl flex-1 p-6">
        <UAlert
          v-if="notice"
          class="mb-6"
          color="success"
          variant="soft"
          icon="i-lucide-check-circle"
          :title="notice"
        />
        <slot />
      </main>
    </div>
  </UApp>
</template>
